import classNames from 'classnames';
import type { PropsWithChildren, ReactNode } from 'react';

import { MarkdownPreview } from '@/common/components/shared/markdown/MarkdownPreview';

interface Props {
  index: number;
  title: ReactNode;
  actions?: ReactNode;
  description?: string | null;
  hideBottomBorder?: boolean;
  label?: string;
  layout?: 'vertical' | 'horizontal';
  titleBorder?: boolean;
  useMarkdown?: boolean;
  weight?: number | null;
  weightAdditional?: ReactNode;
  weightWrapperClassName?: string;
}

export const WeightedItemContainer = ({
  actions,
  children,
  description,
  hideBottomBorder,
  index,
  label = 'Input measures and description',
  layout = 'vertical',
  title,
  titleBorder,
  useMarkdown,
  weight,
  weightAdditional,
  weightWrapperClassName,
}: PropsWithChildren<Props>) => (
  <div className="flex flex-col space-y-4">
    <div className="flex w-full justify-between">
      <div className="flex space-x-2">
        <div className="flex min-w-6 max-w-6 size-6 items-center justify-center rounded-full bg-gray-9">
          <span className="text-white">{index + 1}</span>
        </div>
        <span className="font-semibold leading-11">{title}</span>
      </div>

      <div
        className={classNames('flex', 'ml-auto', {
          'items-center': !weightAdditional,
          'items-start': !!weightAdditional,
        })}
      >
        {actions}
        <div className={classNames('ml-1', weightWrapperClassName)}>
          {weight !== null && (
            <div className="flex items-center justify-end text-xs">
              <span className="uppercase">Weight:&nbsp;</span>
              <span className="w-7 text-right tabular-nums">{weight}</span>%
            </div>
          )}
          {weightAdditional}
        </div>
      </div>
    </div>
    {titleBorder && description && <hr className="-mx-4" />}

    {description && (
      <div
        className={classNames('flex', {
          'flex-col': layout === 'vertical',
          'flex-row gap-1': layout === 'horizontal',
        })}
      >
        <div
          className={classNames('text-gray-7', {
            'pt-1': layout === 'horizontal' && useMarkdown,
          })}
        >
          {label}:
        </div>
        <MarkdownPreview text={description} useMarkdown={useMarkdown} />
      </div>
    )}

    {!titleBorder && !hideBottomBorder && <hr className="-mx-4" />}

    {children}
  </div>
);
